import { useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { postToParent } from 'v2/pages/embed/embed-messaging'
import { ensureEmbedPhone } from 'v2/pages/embed/embed-phone'
import { useFeatureFlags } from 'v2/shared/hooks/use-feature-flags'
import { hodorService } from 'v2/shared/services/hodor-service'
import { getEmbedSession } from 'v2/shared/utils/embed-session'
import { checkMicrophone, MICROPHONE_ERROR_TEXT } from 'v2/shared/utils/microphone-check'
import { getSettingsQueryKeys } from 'v2/shared/utils/query-key-management'
import { voiceAudioConstraints } from 'v2/utils/phone-sdk/utils/helpers'
import { embedTestErrorToast, toastRequestError } from '../utils/embed-test-agent'

// Readies an embed test call before anything is saved or started: the microphone, the session
// user's SIP endpoint (created on its first fetch, so fetched only here) and the phone socket.
export const useEmbedVoiceTest = () => {
  const queryClient = useQueryClient()
  const { channelVoiceEnabled } = useFeatureFlags()

  return useCallback(async (): Promise<boolean> => {
    const mic = await checkMicrophone()
    if (mic !== 'ok') {
      embedTestErrorToast(MICROPHONE_ERROR_TEXT[mic])
      // The host page may own the fix (its Permissions-Policy, or its own help).
      postToParent({ type: 'embed:mic-error', reason: mic, message: MICROPHONE_ERROR_TEXT[mic] })
      return false
    }
    const sessionId = getEmbedSession()?.session_id
    if (!sessionId) {
      return false
    }
    try {
      const creds = await queryClient.fetchQuery({
        queryKey: getSettingsQueryKeys().embedSipCredentials(sessionId),
        queryFn: async () => (await hodorService.getEmbedSipCredentials()).data,
        staleTime: Number.POSITIVE_INFINITY,
        // A click is the retry: backoff would leave the button spinning for seconds.
        retry: false,
      })
      if (!creds?.username || !creds.password) {
        throw new Error('useEmbedVoiceTest: the credentials response has no username or password')
      }
      if (!(await ensureEmbedPhone(creds, voiceAudioConstraints(channelVoiceEnabled)))) {
        embedTestErrorToast("Couldn't connect the test call. Check your network and try again.")
        return false
      }
      return true
    } catch (error) {
      toastRequestError(error)
      return false
    }
  }, [queryClient, channelVoiceEnabled])
}
